// JOBE-0162: Organization (W3) page — structure-first directory.
//
// Rebuild to match the W3 wireframe in
// docs/proposals/jobe-webui-workforce-navigation/wireframes.html
// (pane-org). Paper aesthetic: frame with stamp shadow, subhead
// (crumb + serif title + segmented controls), KPI strip, search/filter
// toolbar, dept-card grid (OrganizationRoster), reporting & collaboration
// tree, and footer keybindings strip.
//
// Data plumbing preserved:
//   - useOrganizationPeople() (wcc.mock.organization flag gate)
//   - ?type, ?dept, ?ownerId URL state via useOrganizationUrlState
//   - OWNS-N chip + drillContext + applyFilters
// Visual rewrite only.

import React, { useMemo, useState } from 'react';
import { PillChip } from '../components/PillChip/PillChip.jsx';
import { useOrganizationPeople } from './fetchOrganizationPeople.js';
import { useOrganizationUrlState } from './useOrganizationUrlState.js';
import { OrganizationFilters } from './OrganizationFilters.jsx';
import { OrganizationRoster, applyFilters } from './OrganizationRoster.jsx';
import { lookupOwner, countOwnedBy } from './ownership.js';
import { OrganizationSearch } from './OrganizationSearch.jsx';
import { OrganizationEmpty } from './OrganizationEmpty.jsx';
import { RosterSkeleton } from './RosterSkeleton.jsx';
import { useDebouncedValue } from './useDebouncedValue.js';
import { withDrillContext } from '../lib/drillContext.js';
import './organization.css';

// ── KPI strip ────────────────────────────────────────────────────────

function computeKpis(rows) {
  const agents = rows.filter((r) => r.type === 'agent');
  const humans = rows.filter((r) => r.type === 'human');
  const depts = new Set(rows.map((r) => r.dept).filter(Boolean));
  const owners = humans.filter((h) => countOwnedBy(rows, h.id) > 0);
  const soleOwner = owners.length === 1 ? owners[0] : null;
  const degraded = agents.filter((a) => a.phase === 'Degraded').length;
  const disabled = agents.filter((a) => a.phase === 'Disabled').length;
  const available = agents.filter((a) => a.phase === 'Available').length;
  const total = agents.length;
  const coveragePct = total === 0 ? 0 : Math.round((available / total) * 100);

  return {
    agentsTotal: total,
    deptCount: depts.size,
    ownersCount: owners.length,
    soleOwner,
    available,
    degraded,
    disabled,
    coveragePct,
    humans: humans.length,
  };
}

function KpiStrip({ rows, mock }) {
  const k = useMemo(() => computeKpis(rows), [rows]);
  return (
    <div className="org-strip" data-testid="organization-kpi-strip">
      <div className="kpi">
        <div className="lab">
          <b>Durable agents</b>
        </div>
        <div className="big">
          {k.agentsTotal}{' '}
          <small>
            across {k.deptCount} dept{k.deptCount === 1 ? '' : 's'}
          </small>
        </div>
        <div className="rows">
          <div className="r">
            <span className="n">{k.humans}</span>
            <span>humans on the roster</span>
          </div>
          <div className="r">
            <span className="n">{mock ? 'mock' : 'live'}</span>
            <span>data source</span>
          </div>
        </div>
      </div>
      <div className="kpi">
        <div className="lab">
          <b>Owner</b>
        </div>
        <div className="big mono">
          {k.soleOwner ? k.soleOwner.displayName.split(' ')[0].toLowerCase() : `${k.ownersCount}`}
        </div>
        <div className="rows">
          <div className="r">
            <span className="n">{k.ownersCount}</span>
            <span>
              human{k.ownersCount === 1 ? '' : 's'}
              {k.ownersCount === 1 ? ' · sole owner' : ' own agents'}
            </span>
          </div>
          <div className="r">
            <span className="n">{k.humans}</span>
            <span>humans total</span>
          </div>
        </div>
      </div>
      <div className="kpi">
        <div className="lab">
          <b>Agent state</b>
        </div>
        <div className="big">
          {k.available}{' '}
          <small>available</small>
        </div>
        <div className="rows">
          <div className="r">
            <span className="n">{k.degraded}</span>
            <span>degraded</span>
          </div>
          <div className="r">
            <span className="n">{k.disabled}</span>
            <span>disabled</span>
          </div>
        </div>
      </div>
      <div className="kpi">
        <div className="lab">
          <b>Coverage</b>
        </div>
        <div className="big">
          {k.coveragePct}%{' '}
          <small>agents available</small>
        </div>
        <div className="rows">
          <div className="r">
            <span className="n">{k.deptCount}</span>
            <span>departments</span>
          </div>
        </div>
        <div className="mini-bar" aria-hidden="true">
          <i style={{ width: `${k.coveragePct}%` }} />
        </div>
      </div>
    </div>
  );
}

// ── Reporting & collaboration ────────────────────────────────────────

function ReportingTree({ rows, drillContext }) {
  const groups = useMemo(() => {
    const humans = rows.filter((r) => r.type === 'human');
    const owners = humans
      .map((h) => ({
        human: h,
        ownedAgents: rows.filter((r) => r.type === 'agent' && r.ownerId === h.id),
      }))
      .filter((g) => g.ownedAgents.length > 0);
    // Sort by owned count desc so the heaviest-owners surface first.
    owners.sort((a, b) => b.ownedAgents.length - a.ownedAgents.length);
    return owners;
  }, [rows]);

  if (groups.length === 0) return null;

  return (
    <section className="org-body" data-testid="org-reporting">
      <div className="blockHead">
        <h3>
          Reporting &amp; collaboration{' '}
          <em>— who owns whom, who works with whom</em>
        </h3>
        <span className="meta">
          {groups.reduce((n, g) => n + g.ownedAgents.length, 0)} edges
        </span>
      </div>
      <div className="org-reporting">
        <div className="org-tree">
          {groups.map((g) => (
            <div className="branch" key={g.human.id}>
              <div className="label">
                owns →
                <br />
                <b>{g.human.displayName.toLowerCase()}</b>
              </div>
              <div className="row-out">
                {g.ownedAgents.map((agent) => (
                  <a
                    key={agent.id}
                    className="agent-chip"
                    href={withDrillContext(`/agent/${agent.id}`, drillContext)}
                    data-testid={`org-tree-chip-${agent.id}`}
                  >
                    <span
                      className={`d${
                        agent.phase === 'Degraded'
                          ? ' watch'
                          : agent.phase === 'Disabled'
                            ? ' block'
                            : ''
                      }`}
                    />
                    {agent.displayName.toLowerCase()}
                    <span className="dept">{agent.dept}</span>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Page ─────────────────────────────────────────────────────────────

export function OrganizationPage() {
  const { rows, loading, mock } = useOrganizationPeople();
  const {
    typeFilter,
    deptFilter,
    ownerId,
    setTypeFilter,
    setDeptFilter,
    setOwnerId,
  } = useOrganizationUrlState();

  // Search state is transient (not URL-mirrored in v1 — see spec).
  const [queryRaw, setQueryRaw] = useState('');
  const query = useDebouncedValue(queryRaw, 150);

  const visible = applyFilters(rows, { typeFilter, deptFilter, ownerId, query });
  const drillContext = {
    from: 'organization',
    filters: { type: typeFilter, dept: deptFilter, ownerId },
  };
  const owner = ownerId ? lookupOwner(rows, ownerId) : null;

  const hasNoData = !loading && rows.length === 0;
  const showEmpty = !loading && (hasNoData || visible.length === 0);
  const showRoster = !loading && visible.length > 0;

  return (
    <div className="organization-page" data-testid="organization-page">
      <header className="organization-header">
        <div>
          <div className="crumb">
            Workforce / <b>Organization</b>
          </div>
          <h1>
            Organization <span className="sub">— structure &amp; roles</span>
            {mock && <PillChip label="MOCK" />}
          </h1>
        </div>
      </header>

        {!loading && rows.length > 0 ? <KpiStrip rows={rows} mock={mock} /> : null}

        <div className="organization-toolbar">
          <div data-testid="organization-search-slot">
            <OrganizationSearch value={queryRaw} onChange={setQueryRaw} />
          </div>
          <div data-testid="organization-filters-slot">
            <OrganizationFilters
              rows={rows}
              typeFilter={typeFilter}
              deptFilter={deptFilter}
              onTypeChange={setTypeFilter}
              onDeptChange={setDeptFilter}
            />
          </div>
        </div>

        {ownerId ? (
          <div className="org-owner-banner" data-testid="org-owner-banner">
            Showing rows owned by{' '}
            <b data-testid="org-owner-banner-name">
              {owner?.displayName || ownerId}
            </b>
            <button
              type="button"
              className="org-owner-banner-clear"
              data-testid="org-owner-banner-clear"
              onClick={() => setOwnerId(null)}
            >
              Clear
            </button>
          </div>
        ) : null}

        <div className="org-body" data-testid="organization-roster-slot">
          <div className="blockHead">
            <h2>
              Departments <em>— click for detail</em>
            </h2>
            <span className="meta">
              {visible.length} row{visible.length === 1 ? '' : 's'}
              {typeFilter !== 'all' || deptFilter !== 'all' || query || ownerId
                ? ' · filtered'
                : ''}
            </span>
          </div>

          {loading ? <RosterSkeleton /> : null}
          {showEmpty ? (
            <OrganizationEmpty
              typeFilter={typeFilter}
              deptFilter={deptFilter}
              query={query}
              ownerId={ownerId}
              totalRows={rows.length}
            />
          ) : null}
          {showRoster ? (
            <OrganizationRoster
              rows={visible}
              allRows={rows}
              loading={loading}
              drillContext={drillContext}
              onOwnerChipClick={(row) => setOwnerId(row.id)}
            />
          ) : null}
        </div>

        {!loading && rows.length > 0 ? (
          <ReportingTree rows={rows} drillContext={drillContext} />
        ) : null}

        <footer className="organization-footkeys" aria-hidden="true">
          <div className="group">
            <b>Keys</b>
          </div>
          <div className="group">
            <kbd>j</kbd>
            <kbd>k</kbd> dept
          </div>
          <div className="group">
            <kbd>↵</kbd> open dept
          </div>
          <div className="group">
            <kbd>/</kbd> search
          </div>
          <div className="group">
            <kbd>v</kbd> switch view
          </div>
          <div className="group spacer">
            {mock ? 'mock fixture · 25 rows' : 'directory · synced'}
          </div>
        </footer>
    </div>
  );
}
